import { useMemo, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchErsatzteile, fetchErsatzteilarten, type Ersatzteil } from "@/lib/api"
import { Button } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import { Sheet, SheetContent, SHEET_WIDTH } from "@/components/Sheet"
import { ErsatzteilDetailContent } from "./ErsatzteilDetailPage"

/** Ersatzteil-Liste (AP-3.5, 02-L). Anlegen/Bearbeiten/Ansehen geschieht im
 * Sheet von rechts (UX_KONZEPT.md §7); `/maschinen/ersatzteile/:id` bzw.
 * `/neu` bleiben als Deep-Link-Fallback erhalten (ErsatzteilDetailPage). */
export function ErsatzteilePage() {
  const [detailErsatzteilId, setDetailErsatzteilId] = useState<number | "neu" | null>(null)
  const queryClient = useQueryClient()
  const { data: ersatzteile, isLoading, isError, refetch } = useQuery({
    queryKey: ["ersatzteile"],
    queryFn: () => fetchErsatzteile(),
  })
  const { data: arten } = useQuery({ queryKey: ["ersatzteilarten"], queryFn: fetchErsatzteilarten })

  const artName = (id: number | null) =>
    id === null ? "—" : arten?.find((a) => a.id === id)?.name ?? `Art #${id}`

  const sheetSchliessen = () => setDetailErsatzteilId(null)

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const spalten = useMemo<ColumnDef<Ersatzteil>[]>(
    () => [
      {
        id: "bezeichnung",
        header: "Bezeichnung",
        accessorFn: (e) => e.bezeichnung,
        cell: ({ row }) => row.original.bezeichnung,
        meta: { cellClassName: "text-slate-900" },
      },
      {
        id: "ersatzteilart",
        header: "Ersatzteilart",
        accessorFn: (e) => (e.ersatzteilartId === null ? "" : artName(e.ersatzteilartId)),
        cell: ({ row }) => artName(row.original.ersatzteilartId),
      },
      {
        id: "spsZuordnung",
        header: "SPS-Zuordnung",
        accessorFn: (e) => e.spsZuordnung ?? "",
        cell: ({ row }) => row.original.spsZuordnung ?? "—",
      },
      {
        id: "ersatzWirdVerwendet",
        header: "Ersatz wird verwendet",
        accessorFn: (e) => (e.ersatzWirdVerwendet ? "ja" : "nein"),
        cell: ({ row }) => (row.original.ersatzWirdVerwendet ? "ja" : "nein"),
      },
    ],
    // artName hängt an den Query-Daten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [arten],
  )

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Ersatzteile</h1>
          <p className="mt-1 text-sm text-slate-600">
            Bis zu 3 Lieferanten pro Ersatzteil (Stammdaten-API, §7.2).
          </p>
        </div>
        <Button type="button" onClick={() => setDetailErsatzteilId("neu")}>
          + Neues Ersatzteil
        </Button>
      </header>

      <Sheet open={detailErsatzteilId !== null} onOpenChange={(open) => !open && sheetSchliessen()}>
        <SheetContent className="overflow-y-auto" sheetKey="ersatzteil-detail" defaultWidth={SHEET_WIDTH.xl}>
          {detailErsatzteilId !== null && (
            <ErsatzteilDetailContent
              ersatzteilId={detailErsatzteilId === "neu" ? null : detailErsatzteilId}
              onGespeichert={(ergebnis) => {
                queryClient.invalidateQueries({ queryKey: ["ersatzteile"] })
                setDetailErsatzteilId(ergebnis.id)
              }}
              onGeloescht={sheetSchliessen}
            />
          )}
        </SheetContent>
      </Sheet>

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Ersatzteile …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-700">
          Ersatzteile konnten nicht geladen werden.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </p>
      )}

      {ersatzteile && (
        <DataGrid
          columns={spalten}
          data={ersatzteile}
          storageKey="ersatzteile"
          onRowClick={(e) => setDetailErsatzteilId(e.id)}
          emptyMessage="Keine Ersatzteile erfasst."
        />
      )}
    </div>
  )
}
